import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';
import { apiFetch, setSessionToken } from '../lib/apiClient';
import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';

const AUTH_PROVIDER = (process.env.REACT_APP_AUTH_PROVIDER || 'supabase').toLowerCase();

/** Survives React Strict Mode remounts so we do not exchange the same PKCE code twice. */
let lastExchangedAuthCode = null;
/** Prevents duplicate processAuth runs across Strict Mode remounts. */
let callbackAuthInFlight = false;

const CAPACITOR_CALLBACK_PREFIX = 'capacitor://localhost/auth/callback';
const CUSTOM_SCHEME_AUTH_BASE =
  process.env.REACT_APP_CUSTOM_SCHEME_AUTH || 'skipdq://auth/callback';

function getCallbackUrl() {
  const href = window.location.href;
  if (href.startsWith(CAPACITOR_CALLBACK_PREFIX)) {
    return new URL(href.replace('capacitor://localhost', 'http://localhost'));
  }
  return new URL(href);
}

function isCapacitorAppShell() {
  if (typeof window.Capacitor === 'undefined' || !window.Capacitor.isNativePlatform) {
    return false;
  }
  if (!window.Capacitor.isNativePlatform()) return false;
  const { hostname, protocol } = window.location;
  if (protocol === 'capacitor:' || hostname === 'localhost') return true;
  if (/^127\.\d+\.\d+\.\d+$/.test(hostname)) return true;
  return false;
}

/**
 * True when OAuth finished in SFSafariViewController / Custom Tabs on a hosted HTTPS URL
 * (not the Capacitor WebView). We must not require origin === a single env URL — www, custom
 * domains, and *.vercel.app previews would otherwise skip the skipdq:// handoff and leave
 * the user in the browser.
 */
function isNativeInAppBrowserHandoff() {
  if (typeof window === 'undefined') return false;
  if (isCapacitorAppShell()) return false;

  const { protocol, hostname, pathname } = window.location;
  if (protocol !== 'https:' && protocol !== 'http:') return false;
  if (hostname === 'localhost' || /^127\.\d+\.\d+\.\d+$/.test(hostname)) return false;

  if (!pathname.includes('auth/callback')) return false;

  const params = new URLSearchParams(window.location.search || '');
  return params.get('cap_app') === '1';
}

/** iOS WebKit in SFSafariViewController sometimes needs replace + synthetic navigation for custom schemes. */
function navigateToAppDeepLink(url) {
  window.location.replace(url);
  window.setTimeout(() => {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.setAttribute('target', '_self');
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, 250);
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth, setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current || callbackAuthInFlight) return;
    hasProcessed.current = true;
    callbackAuthInFlight = true;

    const processAuth = async () => {
      try {
        if (AUTH_PROVIDER !== 'legacy' && isSupabaseConfigured && supabase) {
          const pageUrl = new URL(window.location.href);

          if (isNativeInAppBrowserHandoff()) {
            // PKCE code_verifier lives only in the Capacitor WebView — do NOT exchange on Vercel.
            // Forward ?code= to the app; main WebView runs exchangeCodeForSession.
            const authCode = pageUrl.searchParams.get('code');
            if (authCode) {
              const handoff = new URLSearchParams({ code: authCode });
              navigateToAppDeepLink(`${CUSTOM_SCHEME_AUTH_BASE}?${handoff.toString()}`);
              return;
            }

            const hashParams = new URLSearchParams(
              (window.location.hash || '').replace(/^#/, ''),
            );
            const access_token = hashParams.get('access_token');
            const refresh_token = hashParams.get('refresh_token');
            if (access_token && refresh_token) {
              const fragment = new URLSearchParams({
                access_token,
                refresh_token,
              }).toString();
              navigateToAppDeepLink(`${CUSTOM_SCHEME_AUTH_BASE}#${fragment}`);
              return;
            }

            console.error('Native handoff: missing code (PKCE) or tokens in URL');
            navigate('/', { replace: true });
            return;
          }

          const callbackUrl = getCallbackUrl();
          const queryParams = callbackUrl.searchParams;
          const authCode = queryParams.get('code');

          let sessionData = null;
          let sessionError = null;

          if (authCode) {
            if (lastExchangedAuthCode === authCode) {
              const { data, error } = await supabase.auth.getSession();
              sessionData = data;
              sessionError = error;
            } else {
              const { data, error } = await supabase.auth.exchangeCodeForSession(authCode);
              sessionData = data;
              sessionError = error;
              if (!error && data?.session) {
                lastExchangedAuthCode = authCode;
              }
            }
          } else {
            const { data, error } = await supabase.auth.getSession();
            sessionData = data;
            sessionError = error;
          }

          const accessToken = sessionData?.session?.access_token;
          if (sessionError || !accessToken) {
            console.error('Supabase auth failed:', sessionError);
            navigate('/', { replace: true });
            return;
          }
          setUser(null);
          setSessionToken(accessToken);
          await checkAuth();
          navigate('/', { replace: true });
          return;
        }

        const callbackUrl = getCallbackUrl();
        const hashParams = new URLSearchParams(callbackUrl.hash ? callbackUrl.hash.substring(1) : '');
        const queryParams = callbackUrl.searchParams;
        const sessionId = hashParams.get('session_id') || queryParams.get('session_id');
        if (!sessionId) {
          navigate('/', { replace: true });
          return;
        }

        const response = await apiFetch('/api/auth/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          console.error('Legacy auth failed');
          navigate('/', { replace: true });
          return;
        }

        const userData = await response.json();
        setUser(null);
        if (userData?.session_token) {
          setSessionToken(userData.session_token);
        }
        await checkAuth();
        navigate('/', { replace: true });
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/', { replace: true });
      } finally {
        callbackAuthInFlight = false;
      }
    };

    processAuth();
  }, [checkAuth, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
